import React, { memo, useEffect } from 'react';
import jsPDF from 'jspdf';
import moment from 'moment';
import { addPageNumber } from '../common/addPageNumber';
import autoTable from 'jspdf-autotable';

interface ScopeTimeSheet {
    data: any;
    setPdfBlob: (blob: Blob) => void;
}

const ScopeTimeSheet: React.FC<ScopeTimeSheet> = ({ data, setPdfBlob }) => {
    const generatePDF = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4',
        });
        const sortedData = [...data.scopeTimesheetData].sort((a, b) =>
            a.FullName.localeCompare(b.FullName)
        );
        // Group data by PersonID
        const groupedByPerson = sortedData.reduce((acc: any, entry: any) => {
            if (!acc[entry.PersonID]) {
                acc[entry.PersonID] = [];
            }
            acc[entry.PersonID].push(entry);
            return acc;
        }, {});

        Object.entries(groupedByPerson).forEach(([personId, personEntries]: any, personIndex: number) => {
            if (personIndex > 0) {
                doc.addPage();
            }

            // Group entries by WeekNumber for this person
            const groupedByWeek = personEntries.reduce((acc: any, entry: any) => {
                if (!acc[entry.WeekNumber]) {
                    acc[entry.WeekNumber] = [];
                }
                acc[entry.WeekNumber].push(entry);
                return acc;
            }, {});

            const firstEntry = personEntries[0];
            const pageWidth = doc.internal.pageSize.width;
            const margin = 10;
            let y = margin;

            // Header information (only once per person)
            doc.setFont("helvetica", "normal");
            doc.setFontSize(18);
            doc.text("SCOPE Time Sheet", margin, y);
            y += 8;
            doc.setFontSize(12);
            doc.text("Name:", margin, y);
            doc.text(firstEntry.FullName || '', margin + 20, y);
            doc.text("Position:", pageWidth / 2, y);
            doc.text(firstEntry.Position || '', pageWidth / 2 + 30, y);
            y += 6;

            doc.text("District:", margin, y);
            doc.text(firstEntry.DistrictName, margin + 20, y);
            doc.text("Budget Code:", pageWidth / 2, y);
            doc.text(String(firstEntry.DIST_NUM || ''), pageWidth / 2 + 30, y);
            y += 6;

            doc.text("Building:", margin, y);
            doc.text(``, margin + 22, y);
            doc.text("Program:", pageWidth / 2, y);
            doc.text(firstEntry.SiteName || '', pageWidth / 2 + 30, y);
            y += 6;

            doc.text("Schedule:", margin, y);
            doc.text(firstEntry.Schedule || '', margin + 20, y);
            y += 8;

            // Generate tables for each week
            Object.entries(groupedByWeek).forEach(([weekNumber, weekEntries]: any, weekIndex: number) => {
                if (weekIndex > 0) {
                    y += 10; // Add some space between weeks
                }
                y += 5;
                if (y + 5 > doc.internal.pageSize.height - 10) {
                    doc.addPage();
                    y = 20;
                }
                doc.setFont("helvetica", "bold");
                doc.text(`Week# ${weekNumber}`, margin, y);
                doc.setFont("helvetica", "normal");
                doc.setFont("helvetica", "bold");
                doc.setFontSize(14);
                doc.text("Program Hours", margin + 100, y);
                doc.setFontSize(10);
                doc.setFont("helvetica", "normal");
                doc.text("Maximum Weekly Program Hours.", margin + 90, y + 5);
                y += 10;

                // Timesheet table for this week
                const tableData = weekEntries.flatMap((entry: any) => {
                    const row = [
                        entry.SiteName || '',
                        entry.TimeSheetDate ? moment(entry.TimeSheetDate).format('MM/DD/YYYY') : '', // Date
                        entry.TimeIn ? moment(entry.TimeIn, 'HH:mm:ss').format('hh:mm A') : '',
                        entry.ClockInLocal ? moment(entry.ClockInLocal, 'HH:mm:ss').format('hh:mm A') : '',
                        entry.TimeOut ? moment(entry.TimeOut, 'HH:mm:ss').format('hh:mm A') : '',
                        entry.ClockOutLocal ? moment(entry.ClockOutLocal, 'HH:mm:ss').format('hh:mm A') : '',
                        //entry.LunchOut ? moment(entry.LunchOut, 'HH:mm:ss').format('hh:mm A') : '',
                        //entry.LunchIn ? moment(entry.LunchIn, 'HH:mm:ss').format('hh:mm A') : '',
                        entry.AdditionalStart ? moment(entry.AdditionalStart, 'HH:mm:ss').format('hh:mm A') : '',
                        entry.AdditionalStop ? moment(entry.AdditionalStop, 'HH:mm:ss').format('hh:mm A') : '',
                        '', // Empty placeholders (for future use or to maintain table structure)
                        '',
                        (entry.WorkingHours + entry.AdditionalHours).toFixed(2) // Calculated total hours
                    ];


                    // Add notes row if NotesHeader exists for this entry
                    if (entry.NotesHeader) {
                        return [
                            row,
                            [
                                { content: entry.NotesHeader ? entry.NotesHeader : '', colSpan: 1, styles: { fontStyle: 'bold' } },
                                { content: entry.NotesDetails ? entry.NotesDetails : '', colSpan: 12, styles: { textColor: '#474747' } }
                            ]
                        ];
                    }
                    return [row];
                });

                autoTable(doc, {
                    head: [['Program', 'Date', 'Clock In', 'System In', 'Clock Out', 'System Out', 'Start', 'End', 'Explanation', 'Code', 'Hours']],
                    //head: [['Program', 'Date', 'Clock In', 'System In', 'Clock Out', 'System Out', 'Lunch Out', 'Lunch In', 'Start', 'End', 'Explanation', 'Code', 'Hours']],
                    body: tableData,
                    startY: y,
                    theme: 'grid',
                    styles: {
                        fontSize: 8,
                        cellPadding: 1,
                        lineColor: 40,
                        lineWidth: 0.1,
                    },
                    headStyles: {
                        fontStyle: 'bold',
                        textColor: 0,
                        fillColor: false,
                    },
                    columnStyles: {
                        0: { cellWidth: 30 },
                        1: { cellWidth: 20 },
                        2: { cellWidth: 20 },
                        3: { cellWidth: 20 },
                        4: { cellWidth: 20 },
                        5: { cellWidth: 20 },
                        //6: { cellWidth: 20 },
                        //7: { cellWidth: 20 },
                        6: { cellWidth: 20 },
                        7: { cellWidth: 20 },
                        8: { cellWidth: 25 },
                        9: { cellWidth: 15 },
                        10: { cellWidth: 15 }
                    },
                    pageBreak: 'auto',
                });

                y = (doc as any).lastAutoTable.finalY + 10;
                y += 5;
            });

            const maxPageHeight = doc.internal.pageSize.height; // Height of the page in mm
            const lineHeight = 10; // Estimate for line height

            // Check if space is available before adding total hours and signature sections
            const checkAndAddPage = (y: number) => {
                if (y + lineHeight > maxPageHeight - margin) {
                    doc.addPage();
                    y = margin; // Reset y to the top margin
                }
                return y;
            };

            const totalProgramHours = personEntries.reduce((sum: any, entry: any) => sum + entry.WorkingHours, 0);
            const totalAdditionalHours = personEntries.reduce((sum: any, entry: any) => sum + entry.AdditionalHours, 0);
            const totalHours = totalProgramHours + totalAdditionalHours;
            y = checkAndAddPage(y);
            doc.text(`Program Hours:${totalHours.toFixed(2)}`, margin + 140, y - 5);
            // Totals and signature lines (once per person, after all weeks)
            doc.text("Total Absences:", margin, y);
            y = checkAndAddPage(y);
            doc.line(margin + 30, y + 1, pageWidth / 3 - 10, y + 1);
            doc.text("Total Lateness:", pageWidth / 3, y);
            doc.line(pageWidth / 3 + 30, y + 1, 2 * pageWidth / 3 - 10, y + 1);
            doc.text("Total Left early:", 2 * pageWidth / 3, y);
            doc.line(2 * pageWidth / 3 + 30, y + 1, pageWidth - margin, y + 1);
            y += 10;
            y = checkAndAddPage(y);

            // Certification and signatures
            doc.setFontSize(9);
            doc.text("I certify that the above account is true and correct and that the services performed were rendered to SCOPE on the dates and hours stated.", margin, y);
            y += 10;
            y = checkAndAddPage(y);
            doc.text("Employee Signature:", margin, y);
            doc.line(margin + 40, y, pageWidth / 3.5, y);
            doc.text("Date:", pageWidth / 3, y);
            doc.line(pageWidth / 2, y, pageWidth / 2.5 - margin, y);
            y += 10;
            y = checkAndAddPage(y);
            doc.text("Verification Signature:", margin, y);
            doc.line(margin + 40, y, pageWidth / 3.5, y);
            doc.text("Date:", pageWidth / 3, y);
            doc.line(pageWidth / 2, y, pageWidth / 2.5 - margin, y);
            doc.text("(for Program Hours)", margin, y + 5);
            y = checkAndAddPage(y);
            // SCOPE Office box
            doc.rect(pageWidth - 125, y - 5, 30, 20);
            doc.text("SCOPE Office:", pageWidth - 125, y - 10);

            y = checkAndAddPage(y);
            doc.text("Program Hours:", margin + 200, y);
            doc.text(`${totalProgramHours.toFixed(2)} Hours`, margin + 225, y);
            y += 5;
            doc.text("Additional Hours:", margin + 200, y);
            doc.text(`${totalAdditionalHours.toFixed(2)} Hours`, margin + 225, y);
            y += 5;
            doc.text("Total Hours:", margin + 200, y);
            doc.text(`${totalHours.toFixed(2)} Hours`, margin + 225, y);
            y += 10;
            y = checkAndAddPage(y);
        });

        addPageNumber(doc);
        const pdfBlob = doc.output('blob');
        setPdfBlob(pdfBlob);
    };

    const formatTime = (time?: string | null) => {
        if (!time) return "";

        // Handle "HH:mm:ss" or "HH:mm"
        const m = moment(time, ["HH:mm:ss", "HH:mm"], true);

        if (!m.isValid()) return "";

        // "h:mm A" => 7:00 AM, 3:30 PM (no leading zero on hour)
        return m.format("h:mm A");
    };

    const generateEmptyPDF = () => {
        // `data` is a single schedule when a site is selected, or the list of every
        // assignment for the person when the site filter is left blank.
        const schedules = Array.isArray(data) ? data : [data];
        if (schedules.length === 0) return;

        const doc = new jsPDF({
            orientation: "landscape",
            unit: "mm",
            format: "a4",
        });

        schedules.forEach((schedule: any, scheduleIndex: number) => {
            if (scheduleIndex > 0) {
                doc.addPage();
            }
            renderSchedulePage(doc, schedule);
        });

        const d = new Date();
        const dateStr = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
        addFooter(doc, dateStr);

        // ---- export ----
        const pdfBlob = doc.output("blob");
        setPdfBlob(pdfBlob);
    };

    const renderSchedulePage = (doc: jsPDF, data: any) => {
        const pageWidth = doc.internal.pageSize.width;
        const margin = 10;
        let y = margin;

        // ---------- HEADER ----------
        doc.setFont("helvetica", "normal");
        doc.setFontSize(18);
        doc.text("SCOPE Employee - Time Sheet", margin, y);
        y += 8;

        doc.setFontSize(11);
        doc.text("Name:", margin, y);
        doc.text(`${data.personel.lastname}, ${data.personel.firstname}`, margin + 20, y);
        doc.text("Position:", pageWidth / 2, y);
        doc.text(`${data.position}`, pageWidth / 2 + 30, y);
        y += 5;

        doc.text("District:", margin, y);
        doc.text(`${data.distName}`, margin + 20, y);
        doc.text("Budget Code:", pageWidth / 2, y);
        doc.text(`${data.distNumber}`, pageWidth / 2 + 30, y);
        y += 5;

        let maxHrs = 0;
        const siteType = (data.siteType || '').toLowerCase();
        if (siteType.includes('before')) {
            maxHrs = data.personel?.maX_HRS_B || 0;
        } else if (siteType.includes('during')) {
            maxHrs = data.personel?.maX_HRS_D || 0;
        } else if (siteType.includes('after')) {
            maxHrs = data.personel?.maX_HRS_A || 0;
        }
        doc.text("Max Hours:", margin, y);
        doc.text(`${maxHrs} a week`, margin + 22, y);
        doc.text("Program:", pageWidth / 2, y);
        doc.text(`${data.siteName}`, pageWidth / 2 + 30, y);
        y += 5;


        const schedule =
            `Mon ${formatTime(data.mondayTimeIn)}-${formatTime(data.mondayTimeOut)} | ` +
            `Tue ${formatTime(data.tuesdayTimeIn)}-${formatTime(data.tuesdayTimeOut)} | ` +
            `Wed ${formatTime(data.wednesdayTimeIn)}-${formatTime(data.wednesdayTimeOut)} | ` +
            `Thu ${formatTime(data.thursdayTimeIn)}-${formatTime(data.thursdayTimeOut)} | ` +
            `Fri ${formatTime(data.fridayTimeIn)}-${formatTime(data.fridayTimeOut)}`;
        doc.text("Schedule:", margin, y);
        doc.text(`${schedule}`, margin + 20, y);
        y += 5;

        doc.text("Comment:", margin, y);
        doc.text("", margin + 20, y);
        y += 5;

        doc.setFont("helvetica", "bold");
        doc.text("IMPORTANT NOTE:", margin, y);
        doc.setFont("helvetica", "normal");
        doc.text("You may be assigned additional hours or a location change which you must accomodate in order to meet legally required staff to student ratio.", margin + 36, y);
        y += 8;

        // ---------- MAIN TABLES (compact) ----------
        const leftTableWidth =
            14 + 22 + 18 + 18 + 26 + 26 + 18; // 142mm
        const gapBetweenTables = 8;
        const rightTableX = margin + leftTableWidth + gapBetweenTables;
        const rightTableWidth = pageWidth - margin - rightTableX;

        const leftTableCenterX = margin + leftTableWidth / 2;
        const rightTableCenterX = rightTableX + rightTableWidth / 2;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        const titlesY = y;
        doc.text("Program Hours", leftTableCenterX, titlesY, { align: "center" });
        doc.text("Additional Hours / Extra Pay", rightTableCenterX, titlesY, {
            align: "center",
        });

        doc.setFontSize(9);
        doc.text("(Record Actual time worked)", leftTableCenterX, titlesY + 5, {
            align: "center",
        });
        doc.text("(Record Actual time worked)", rightTableCenterX, titlesY + 5, {
            align: "center",
        });

        y = titlesY + 8;
        const tableStartY = y;

        const tableStyles = {
            font: "helvetica",
            fontSize: 7,
            cellPadding: 1,
            lineWidth: 0.2,
            lineColor: 0, // autoTable defaults to 200 (light grey) when this is omitted
            minCellHeight: 5,
        } as const;

        const tableHeadStyles = {
            fillColor: [255, 255, 255],
            textColor: 0,
            halign: "center",
            valign: "middle",
            fontSize: 7,
            minCellHeight: 7,
        } as const;

        // Row heights (mm) for the two main grids only — the legend tables below
        // keep the compact 5/7. Raise these to give staff more room to write;
        // the vertical gaps further down are tightened to pay for the extra height.
        const MAIN_ROW_HEIGHT = 6.5;
        const MAIN_HEAD_HEIGHT = 8;

        const mainTableStyles = { ...tableStyles, minCellHeight: MAIN_ROW_HEIGHT } as const;
        const mainTableHeadStyles = { ...tableHeadStyles, minCellHeight: MAIN_HEAD_HEIGHT } as const;

        // LEFT table
        const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Mon", "Tue", "Wed", "Thu", "Fri"];

        autoTable(doc, {
            startY: tableStartY,
            margin: { left: margin },
            head: [["", "Date", "In", "Out", "Lunch Out", "Lunch In", "#Hours"]],
            body: days.map(d => [d, "", "", "", "", "", ""]),
            theme: "grid",
            headStyles: mainTableHeadStyles,
            bodyStyles: { fillColor: [255, 255, 255] },
            alternateRowStyles: { fillColor: [255, 255, 255] },
            styles: mainTableStyles,
            columnStyles: {
                0: { cellWidth: 14 },
                1: { cellWidth: 22 },
                2: { cellWidth: 18 },
                3: { cellWidth: 18 },
                4: { cellWidth: 26 },
                5: { cellWidth: 26 },
                6: { cellWidth: 18 },
            },
        });
        const leftTableBottom = (doc as any).lastAutoTable.finalY;

        // RIGHT table
        const extraRows = 10;
        const extraBody = Array.from({ length: extraRows }, () => ["", "", "", "", "", ""]);

        autoTable(doc, {
            startY: tableStartY,
            margin: { left: rightTableX },
            head: [["Date", "Start", "Stop", "Explanation / Location", "Code", "#Hours"]],
            body: extraBody,
            theme: "grid",
            headStyles: mainTableHeadStyles,
            bodyStyles: { fillColor: [255, 255, 255] },
            alternateRowStyles: { fillColor: [255, 255, 255] },
            styles: mainTableStyles,
            columnStyles: {
                0: { cellWidth: 20 },
                1: { cellWidth: 16 },
                2: { cellWidth: 16 },
                3: { cellWidth: 45 },
                4: { cellWidth: 8 },
                5: { cellWidth: 15 },
            },
        });
        const rightTableBottom = (doc as any).lastAutoTable.finalY;

        const topTablesBottom = Math.max(leftTableBottom, rightTableBottom);

        // Totals
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text("Total: ____________________", 100, topTablesBottom + 5);
        doc.text("Total: ____________________", 220, topTablesBottom + 5);

        // ---------- BOTTOM: "TIME NOT AT WORK" + CODES ----------
        let startYBottom = topTablesBottom + 9;

        const leftBlockWidth = 170;
        const gap = 6;
        const codeBlockWidth =
            (pageWidth - margin * 2 - leftBlockWidth - gap) / 2;

        const codesX1 = margin + leftBlockWidth + gap;
        const codesX2 = codesX1 + codeBlockWidth;

        // Left big table
        autoTable(doc, {
            startY: startYBottom,
            pageBreak: "avoid",
            rowPageBreak: "avoid",
            margin: { left: margin },
            tableWidth: leftBlockWidth,
            theme: "grid",
            head: [[{
                content: "For documenting time not at work indicate",
                colSpan: 2,
                styles: { halign: "center", fontStyle: "bold" }
            }]],
            body: [
                ["Sick (Personal illness or Immediate Family)", "Jury Duty"],
                ["Personal Business", "Bereavement (Indicate Relationship)"],
                [
                    "School Closed (as Indicated on Calender)",
                    "Emergency Closing (Snow Days, other authorized emergencies)"
                ],
            ],
            styles: tableStyles,
            headStyles: tableHeadStyles,
            bodyStyles: { fillColor: [255, 255, 255] },
            columnStyles: {
                0: { cellWidth: leftBlockWidth * 0.5 },
                1: { cellWidth: leftBlockWidth * 0.5 },
            },
        });
        const notWorkBottom = (doc as any).lastAutoTable.finalY;

        // Code table 1–2
        autoTable(doc, {
            startY: startYBottom,
            pageBreak: "avoid",
            rowPageBreak: "avoid",
            margin: { left: codesX1 },
            tableWidth: codeBlockWidth - 2,
            theme: "grid",
            head: [["Code", "Explanation / Location"]],
            body: [
                ["1", "Assigned Role of Acting Director"],
                ["2", "Assigned to another program site; Permanent sub assigned to a program outside of cluster"],
            ],
            styles: tableStyles,
            headStyles: tableHeadStyles,
            bodyStyles: { fillColor: [255, 255, 255] },
            columnStyles: {
                0: { cellWidth: 10 },
                1: { cellWidth: codeBlockWidth - 12 },
            },
        });
        const codes1Bottom = (doc as any).lastAutoTable.finalY;

        // Code table 3–4
        autoTable(doc, {
            startY: startYBottom,
            pageBreak: "avoid",
            rowPageBreak: "avoid",
            margin: { left: codesX2 },
            tableWidth: codeBlockWidth - 2,
            theme: "grid",
            head: [["Code", "Explanation / Location"]],
            body: [
                ["3", "Group Leader assigned to role of Small Group Leader"],
                ["4", "Group Leader assigned to role of Head of Group"],
            ],
            styles: tableStyles,
            headStyles: tableHeadStyles,
            bodyStyles: { fillColor: [255, 255, 255] },
            columnStyles: {
                0: { cellWidth: 10 },
                1: { cellWidth: codeBlockWidth - 12 },
            },
        });
        const codes2Bottom = (doc as any).lastAutoTable.finalY;

        const bottomSectionEnd = Math.max(notWorkBottom, codes1Bottom, codes2Bottom);

        // ======================================================
        //          NEW: TOTALS + SIGNATURES + OFFICE BOX
        // ======================================================
        let infoY = bottomSectionEnd + 7;

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.text("Total Absences: ________", margin, infoY);
        doc.text("Total Lateness: ________", margin + 70, infoY);
        doc.text("Total Left Early: ________", margin + 130, infoY);

        infoY += 8;
        doc.text(
            "I certify that the above account is true and correct and that the services performed were",
            margin,
            infoY
        );
        infoY += 5;
        doc.text(
            "rendered to SCOPE on the dates and hours stated.",
            margin,
            infoY
        );

        infoY += 9;
        doc.text("Employee Signature: ____________________", margin, infoY);
        doc.text("Date: ____________", margin + 120, infoY);

        infoY += 8;
        doc.text("Verification Signature: ____________________", margin, infoY);
        doc.text("Date: ____________", margin + 120, infoY);

        // ---- Scope Office Use Only box on the right ----
        const boxWidth = 90;
        const boxHeight = 28;
        const boxX = pageWidth - margin - boxWidth;
        const boxY = bottomSectionEnd + 12; // roughly aligned with totals

        // outer box
        doc.rect(boxX, boxY, boxWidth, boxHeight);

        // split left/right inside the box
        const gridWidth = 38; // right area for grid
        const gridX = boxX + boxWidth - gridWidth;

        // vertical line
        doc.line(gridX, boxY, gridX, boxY + boxHeight);

        // horizontal lines inside grid area (4 rows)
        const rowH = boxHeight / 4;
        for (let i = 1; i < 4; i++) {
            const yLine = boxY + rowH * i;
            doc.line(gridX, yLine, boxX + boxWidth, yLine);
        }

        // label in left area
        doc.setFontSize(8);
        doc.text(
            "Scope Office Use Only",
            boxX + (boxWidth - gridWidth) / 2,
            boxY + boxHeight - 5,
            { align: "center" }
        );

        // labels in grid (right side)
        const textX = gridX + 2;
        doc.text("Total Hours Worked:", textX, boxY + rowH * 0.7);
        doc.text("Additional Pay Due:", textX, boxY + rowH * 1.7);
        doc.text("Approved by:", textX, boxY + rowH * 2.7);
        doc.text("Notes:", textX, boxY + rowH * 3.7);
    };

    const addFooter = (doc: jsPDF, dateStr: string) => {
        const pageCount = doc.getNumberOfPages();

        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);

            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 10; // same margin you use elsewhere

            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);

            // left bottom: date
            doc.text(dateStr, margin, pageHeight - 5);

            // right bottom: "Page X of Y"
            doc.text(
                `Page ${i} of ${pageCount}`,
                pageWidth - margin,
                pageHeight - 5,
                { align: "center" }
            );
        }
    };


    const addPageNumber = (doc: jsPDF) => {
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            doc.setFontSize(8);
            doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 5, {
                align: "center",
            });
        }
    };

    useEffect(() => {
        if (!data) return;
        generateEmptyPDF();
    }, [data]);

    return null;
};

export default memo(ScopeTimeSheet);